import { useContext, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router';

import { IntegrationSystem } from '@/shared/types';

import { config } from '@/config/index';

import { MSP_ROUTES_FALLBACK as MSP_ROUTES } from '@/modules/msp/routes/constants';

import { ROUTES } from '@/router/config';

import AuthContext from '@/contexts/Auth/AuthContext';

export const usePageAfterAuth = (): { pageToOpenAfterAuth: string } => {
  const { state } = useLocation();
  const [searchParams] = useSearchParams();

  const { systems } = useContext(AuthContext);

  const effectiveNextPage = (state?.nextPage as Nullable<string>) ?? searchParams.get('next_page');

  const ignoreRedirect = effectiveNextPage?.includes(config.betaTesting.ignoreRedirect);
  const isMSPPriority = useMemo<boolean>(
    () => Boolean(systems?.includes(IntegrationSystem.MSP) && !state?.continueGercPayment),
    [systems, state?.continueGercPayment],
  );

  const isServiceDestination = useMemo<boolean>(() => {
    if (!effectiveNextPage) return false;
    return Object.values(IntegrationSystem).some((system) => effectiveNextPage.startsWith(`/${system}/`));
  }, [effectiveNextPage]);

  const nextPage = useMemo<Nullable<string>>(() => {
    if (ignoreRedirect) {
      return effectiveNextPage;
    }
    if (isMSPPriority) {
      return isServiceDestination ? effectiveNextPage : MSP_ROUTES.SOCIAL_PROTECT;
    }
    return null;
  }, [ignoreRedirect, isMSPPriority, isServiceDestination, effectiveNextPage]);

  const defaultNextPage = !isMSPPriority && !ignoreRedirect ? config.auth.initialPage : null;

  const pageToOpenAfterAuth = useMemo<string>(
    () => nextPage || effectiveNextPage || defaultNextPage || ROUTES.HOME,
    [nextPage, defaultNextPage, effectiveNextPage],
  );

  return {
    pageToOpenAfterAuth,
  };
};
